import React, { useState, useEffect, useRef } from 'react';

import { removeTrailingZeros } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getIntlDate } from '@utils';
import { useModal } from '@utils/hooks';

import { TransactionsTypes, useStore } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { ReferralTransactionsStatusEnum } from '@modules/transactions/types';

import Dropdown from '@components/Dropdown';
import Spinner from '@components/Spinner';

import Clock from './clock.svg';
import { useCustomContext } from '../context';

import style from './RewardsHistory.scss';

const cn = bemCssModules(style, 'RewardsHistory');

const Image: React.FC<{ isRotate?: boolean }> = ({ isRotate }) => (
  <img src="/img/arrow_select.svg" className={cn('button-icon', { isRotate })} alt="arrow" />
);

type TPeriod = {
  [key: string]: boolean;
};

const RewardsHistory: CustomFC = props => {
  const { className } = props;

  const { t } = useTranslation();
  const [{ global, transactions }] = useStore();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isClosedYears, setIsClosedYears] = useState<TPeriod>({});
  const [isClosedMonths, setIsClosedMonths] = useState<TPeriod>({});
  const { list } = transactions.referral;
  const [data, setData] = useState({});
  const [isOpen, setIsOpen, modalRef] = useModal([buttonRef]);
  const { languageCode, currency, setCurrency } = useCustomContext();

  const years = Object.keys(data);
  const getMonths = (year: string) => Object.keys(data[year]);

  const toggleYear = (year: string) => {
    const key = `isClosedYear-${year}`;
    setIsClosedYears(prevState => ({ ...prevState, [key]: !prevState[key] }));
  };

  const toggleMonth = (year: string, month: string) => {
    const key = `isClosedMonth-${year}-${month}`;
    setIsClosedMonths(prevState => ({ ...prevState, [key]: !prevState[key] }));
  };

  useEffect(() => {
    const result = list.reduce((accumulator, transaction) => {
      const [year] = transaction.created_at.split('-');
      const month = getIntlDate(transaction.created_at, { month: 'long' }, languageCode);
      accumulator[year] ??= {};
      accumulator[year][month] ??= [];
      accumulator[year][month].push(transaction);
      return accumulator;
    }, {});
    setData(result);
  }, [list, currency, languageCode]);

  const handleToggleYear = (year: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    toggleYear(year);
  };

  const handleToggleMonth =
    (year: string, month: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      toggleMonth(year, month);
    };

  const handleChangeCurrency = (value: CryptoCurrencyEnum) => {
    setCurrency(value);
    setIsOpen(false);
  };

  const toggleCurrencyModal = () => setIsOpen(!isOpen);

  const isLoading = transactions.loaders.getReferralTransactions && !Object.keys(data).length;

  return (
    <section className={cn(null, [className])}>
      <h2 className={cn('title', ['h1'])}>{t('transactions.rewards_history')}</h2>
      <div className={cn('dropdown-wrapper')}>
        <button
          ref={buttonRef}
          className={cn('button')}
          type="button"
          onClick={toggleCurrencyModal}
        >
          <span className={cn('label')}>{t('transactions.button_transactions', { currency })}</span>
        </button>
        <Dropdown
          className={cn('dropdown')}
          ref={modalRef}
          controller={buttonRef}
          data={global.currencies.crypto}
          value={currency}
          onChange={handleChangeCurrency}
          header={
            <div className={cn('dropdown-title', ['h2'])}>
              {t('invite.currencies_modal.cryptocurrency')}
            </div>
          }
        />
      </div>
      {isLoading ? (
        <div className={cn('loader')}>
          <Spinner />
        </div>
      ) : (
        years.reverse().map(year => {
          const yearIsOpen = !isClosedYears[`isClosedYear-${year}`];
          return (
            <div className={cn('year', { isOpen: yearIsOpen })} key={year}>
              <button type="button" className={cn('year-button')} onClick={handleToggleYear(year)}>
                <div className={cn('year-value')}>{year}</div>
                <Image isRotate={yearIsOpen} />
              </button>
              {yearIsOpen &&
                getMonths(year)
                  .reverse()
                  .map(month => {
                    const id = `${year}-${month}`;
                    const monthIsOpen = !isClosedMonths[`isClosedMonth-${id}`];
                    return (
                      data[year][month].length > 0 && (
                        <div className={cn('month', { isOpen: monthIsOpen })} key={id}>
                          <button
                            type="button"
                            onClick={handleToggleMonth(year, month)}
                            className={cn('month-value')}
                          >
                            <span>{month}</span>
                            <Image isRotate={monthIsOpen} />
                          </button>
                          {monthIsOpen && (
                            <div className={cn('month-list')}>
                              {data[year][month].map(
                                (
                                  transaction: TransactionsTypes.GetReferralTransactionsResponse
                                ) => {
                                  const { created_at, amount } = transaction;
                                  const options = { month: 'short', day: '2-digit' } as const;
                                  const date = getIntlDate(created_at, options, languageCode);
                                  const prettyAmount = removeTrailingZeros(amount);
                                  const value = `+${prettyAmount} ${transaction.currency}`;
                                  const showStatus = [
                                    ReferralTransactionsStatusEnum.new,
                                    ReferralTransactionsStatusEnum.pending,
                                  ].includes(transaction.status);
                                  return (
                                    <div className={cn('transaction')} key={transaction.id}>
                                      <div className={cn('transaction-date-wrapper')}>
                                        <span className={cn('transaction-date', ['small'])}>
                                          {date}
                                        </span>
                                        {showStatus && (
                                          <>
                                            <span className={cn('transaction-dot')} />
                                            <span className={cn('transaction-status', ['small'])}>
                                              {ReferralTransactionsStatusEnum.pending}
                                            </span>
                                            <Clock />
                                          </>
                                        )}
                                      </div>
                                      <span>{value}</span>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      )
                    );
                  })}
            </div>
          );
        })
      )}
    </section>
  );
};

export default RewardsHistory;
